import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { PaperDigestContextProps } from './types';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { interMedium, interRegular, interSemiBold } from '../../lib/fonts';
import { Trans } from '@lingui/react/macro';

export default function PaperDigestContext({ projectPaperContext }: PaperDigestContextProps) {
    const { fontsLoaded } = useFontLoading();
    const [isExpanded, setIsExpanded] = useState(false);
    const {
        recommendationSummary,
        matchingDetails,
        personalizedNote,
    } = projectPaperContext;

    const matchedKeywords = matchingDetails?.matchedKeywords || [];
    const matchedCategories = matchingDetails?.matchedCategories || [];
    const explanation = matchingDetails?.explanation || null;
    const hasMatchDetails = Boolean(
        recommendationSummary
        || explanation
        || matchedKeywords.length > 0
        || matchedCategories.length > 0
        || personalizedNote
    );
    const hasExpandableDetails = Boolean(
        matchedKeywords.length > 0
        || matchedCategories.length > 0
        || personalizedNote
    );

    const summaryLabel = useMemo(() => (
        projectPaperContext.projectName
            ? `Selected for ${projectPaperContext.projectName}`
            : 'Why this paper matched'
    ), [projectPaperContext.projectName]);

    if (!hasMatchDetails) {
        return null;
    }

    return (
        <View style={styles.container} testID="paper-recommendation-context">
            <View style={styles.headerRow}>
                <View style={styles.titleWrap}>
                    <MaterialIcons name="check-circle" size={18} color={colors.primaryGreenDark} />
                    <Text style={[styles.title, fontsLoaded && interSemiBold]}><Trans id="papers.paperdigestcontext.whyThisPaperMatched" comment="UI copy in components/papers/PaperDigestContext.tsx">
                        Why this paper matched
                    </Trans></Text>
                </View>

                {hasExpandableDetails ? (
                    <TouchableOpacity
                        style={styles.expandToggle}
                        onPress={() => setIsExpanded((current) => !current)}
                        activeOpacity={0.76}
                        testID="paper-recommendation-toggle"
                    >
                        <Text style={[styles.expandToggleText, fontsLoaded && interSemiBold]}>
                            {isExpanded ? <Trans id="papers.paperdigestcontext.hideDetails" comment="Conditional UI copy in components/papers/PaperDigestContext.tsx">Hide details</Trans> : <Trans id="papers.paperdigestcontext.showDetails" comment="Conditional UI copy in components/papers/PaperDigestContext.tsx">Show details</Trans>}
                        </Text>
                    </TouchableOpacity>
                ) : null}
            </View>

            {recommendationSummary ? (
                <View style={styles.summaryCard}>
                    <Text style={[styles.summaryLabel, fontsLoaded && interMedium]}>
                        {summaryLabel}
                    </Text>
                    <Text style={[styles.summaryText, fontsLoaded && interRegular]}>
                        {recommendationSummary}
                    </Text>
                </View>
            ) : null}

            {explanation ? (
                <Text style={[styles.explanationText, fontsLoaded && interRegular]}>
                    {explanation}
                </Text>
            ) : null}

            {(isExpanded || !hasExpandableDetails) && (
                <>
                    {matchedKeywords.length > 0 ? (
                        <View style={styles.detailBlock}>
                            <Text style={[styles.detailLabel, fontsLoaded && interSemiBold]}><Trans id="papers.paperdigestcontext.matchedKeywords" comment="UI copy in components/papers/PaperDigestContext.tsx">
                                Matched keywords
                            </Trans></Text>
                            <View style={styles.chipWrap}>
                                {matchedKeywords.map((keyword, index) => (
                                    <View key={`${keyword}-${index}`} style={styles.keywordChip}>
                                        <Text style={[styles.chipText, fontsLoaded && interMedium]}>
                                            {keyword}
                                        </Text>
                                    </View>
                                ))}
                            </View>
                        </View>
                    ) : null}

                    {matchedCategories.length > 0 ? (
                        <View style={styles.detailBlock}>
                            <Text style={[styles.detailLabel, fontsLoaded && interSemiBold]}><Trans id="papers.paperdigestcontext.matchedCategories" comment="UI copy in components/papers/PaperDigestContext.tsx">
                                Matched categories
                            </Trans></Text>
                            <View style={styles.chipWrap}>
                                {matchedCategories.map((category, index) => (
                                    <View key={`${category}-${index}`} style={styles.categoryChip}>
                                        <Text style={[styles.chipText, fontsLoaded && interMedium]}>
                                            {category}
                                        </Text>
                                    </View>
                                ))}
                            </View>
                        </View>
                    ) : null}

                    {personalizedNote ? (
                        <View style={styles.noteCard}>
                            <Text style={[styles.detailLabel, fontsLoaded && interSemiBold]}><Trans id="papers.paperdigestcontext.note" comment="UI copy in components/papers/PaperDigestContext.tsx">
                                Note
                            </Trans></Text>
                            <Text style={[styles.noteText, fontsLoaded && interRegular]}>
                                {personalizedNote}
                            </Text>
                        </View>
                    ) : null}
                </>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        borderRadius: 18,
        backgroundColor: colors.card,
        borderWidth: 1,
        borderColor: colors.border,
        padding: 22,
        gap: 16,
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 12,
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    titleWrap: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    title: {
        fontSize: 19,
        color: colors.text900,
    },
    expandToggle: {
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    expandToggleText: {
        fontSize: 13,
        color: colors.primaryGreenDark,
    },
    summaryCard: {
        padding: 16,
        borderRadius: 14,
        backgroundColor: '#f7fbf2',
        borderWidth: 1,
        borderColor: '#d6e3c8',
        gap: 8,
    },
    summaryLabel: {
        fontSize: 12,
        textTransform: 'uppercase',
        letterSpacing: 0.45,
        color: colors.text500,
    },
    summaryText: {
        fontSize: 15,
        lineHeight: 24,
        color: colors.text900,
    },
    explanationText: {
        fontSize: 15,
        lineHeight: 24,
        color: colors.text700,
    },
    detailBlock: {
        gap: 10,
    },
    detailLabel: {
        fontSize: 13,
        color: colors.text500,
        textTransform: 'uppercase',
        letterSpacing: 0.35,
    },
    chipWrap: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    keywordChip: {
        borderRadius: 999,
        paddingHorizontal: 12,
        paddingVertical: 7,
        backgroundColor: '#f7edd0',
    },
    categoryChip: {
        borderRadius: 999,
        paddingHorizontal: 12,
        paddingVertical: 7,
        backgroundColor: '#e6eff9',
    },
    chipText: {
        fontSize: 13,
        color: colors.text900,
    },
    noteCard: {
        gap: 10,
        padding: 16,
        borderRadius: 14,
        backgroundColor: '#fbf6e9',
    },
    noteText: {
        fontSize: 14,
        lineHeight: 23,
        color: colors.text700,
    },
});
